// Story 1.24 — TanStack Query hook for GET /api/v1/portfolios?status=recruiting.
//
// Backs the browse list on /account/creator-portfolios. Cache for 60s
// + refetch on window focus so the SPA picks up newly-recruiting
// portfolios without a hard refresh.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RecruitingPortfolio = {
  id: string;
  title: string;
  theme: string | null;
  target_creator_count: number | null;
  recruiting_deadline: string | null;
  created_at: string;
  already_applied: boolean;
  /** "pending" | "approved" | null — labels the card truthfully. */
  my_application_status: string | null;
};

export type RecruitingPortfoliosResponse = {
  data: RecruitingPortfolio[];
  page: number;
  page_size: number;
  has_next: boolean;
};

async function fetchRecruitingPortfolios(
  page: number,
): Promise<RecruitingPortfoliosResponse> {
  const response = await fetch(
    `/api/v1/portfolios?status=recruiting&page=${page}&page_size=50`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(
      `Recruiting portfolios fetch failed (HTTP ${response.status})`,
    );
  }
  return (await response.json()) as RecruitingPortfoliosResponse;
}

export function useRecruitingPortfolios(page = 1) {
  return useQuery({
    queryKey: ["creator-portfolios", "recruiting", page],
    queryFn: () => fetchRecruitingPortfolios(page),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
